import { Avatar, Box, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
    IPerson,
    absoluteUserPictureUrl,
    personInitial,
} from '../../library/models/userPicture';
import { memberFullName } from '../../library/membershipDisplay';
import ResourcesIds from '../../resources/ResourcesIds';
import {
    AvatarSize,
    personLabelStyles,
    personNameStyles,
    USER_AVATAR_STYLES,
} from './userAvatar.styles';

/**
 * What to call somebody, whichever shape their payload came in.
 *
 * `memberFullName` still owns the two-column case, including the empty string it returns for
 * a patient invited but not yet signed up -- that case must stay in one place.
 */
const personName = (person: IPerson | null | undefined): string =>
    person?.name?.trim() || (person ? memberFullName(person) : '');

interface IUserAvatarProps {
    /** Anything with a name and maybe a picture. Null draws the anonymous fallback. */
    person: IPerson | null | undefined;
    size?: AvatarSize;
    /**
     * Hides it from assistive technology, for when the person's name is already beside it.
     *
     * `PersonLabel` sets this and standalone callers do not, which is the whole of the rule:
     * an avatar next to "Anna Pop" adds nothing a screen reader should read out -- announcing
     * "A, Anna Pop" for every row of a patient list is noise -- while the one in the app bar
     * stands alone and is the only thing naming who is signed in.
     */
    decorative?: boolean;
}

/**
 * One person, drawn as their picture or as their initial.
 *
 * Shared rather than written at each of the eight places somebody appears, for the reason
 * `RecordCard`'s comment gives about the six lists it serves: without it the same patient is
 * a 32px circle in one list and a 36px square in another, and the fallback letter drifts
 * between "first name only" here and "first name or email" there.
 *
 * **The fallback needs no branch**, which is what makes this small. MUI's `Avatar` renders
 * its children whenever `src` is absent *or* fails to load, so the initial is both the
 * no-picture case and the broken-URL one -- including the window between somebody replacing
 * their picture in one tab and another tab still holding the retired token.
 *
 * The URL is made absolute here rather than by every caller: the API answers with a path
 * relative to itself, and only `config.apiBaseUrl` knows where that is.
 */
export const UserAvatar = ({
    person,
    size = 'sm',
    decorative = false,
}: IUserAvatarProps): JSX.Element =>
    decorative
        ? <DecorativeAvatar person={person} size={size} />
        : <LabelledAvatar person={person} size={size} />;

/**
 * The circle itself. Everything both variants share, and no `useTranslation`.
 *
 * That absence is the point of the split. A decorative avatar's alt is the empty string by
 * definition, so calling into i18n for it is pure waste -- and a paged patient list draws
 * twenty-five of these at a time, which is where the waste stops being theoretical.
 */
const AvatarCircle = ({
    person,
    size,
    alt,
    decorative,
}: IUserAvatarProps & { alt: string }): JSX.Element => (
    <Avatar
        // `undefined` rather than null: MUI passes this straight to <img src>, and null
        // becomes the string "null" and a request for a file that is not there.
        src={absoluteUserPictureUrl(person?.pictureUrl) ?? undefined}
        alt={alt}
        aria-hidden={decorative || undefined}
        sx={USER_AVATAR_STYLES[size ?? 'sm']}
    >
        {personInitial(person)}
    </Avatar>
);

const DecorativeAvatar = ({ person, size }: IUserAvatarProps): JSX.Element => (
    <AvatarCircle person={person} size={size} alt="" decorative />
);

/** Names the person, for the two places an avatar stands on its own. */
const LabelledAvatar = ({ person, size }: IUserAvatarProps): JSX.Element => {
    const { t: translate } = useTranslation();

    const alt: string = person?.pictureUrl
        ? translate(ResourcesIds.PROFILE_PICTURE_ALT, { name: personName(person) })
        : '';

    return <AvatarCircle person={person} size={size} alt={alt} />;
};

interface IPersonLabelProps {
    person: IPerson | null | undefined;
    size?: AvatarSize;
}

/**
 * A person's avatar and their name, side by side.
 *
 * The companion to `memberFullName`, which returns a `string` and therefore cannot carry a
 * picture. Every list that shows people uses this instead of that, so the six of them go on
 * agreeing about what a person looks like.
 *
 * Deliberately renders the name through the same `memberFullName`, rather than composing it
 * again: a patient invited but not yet signed up has neither name, and the empty string that
 * produces is a case that must stay in one place.
 */
export const PersonLabel = ({ person, size = 'sm' }: IPersonLabelProps): JSX.Element => (
    <Box sx={personLabelStyles}>
        <UserAvatar person={person} size={size} decorative />

        {/* The test seam the lists assert through. Without it they read the cell's whole
            textContent, which now begins with the avatar's fallback letter. */}
        <Typography component="span" data-testid="person-name" sx={personNameStyles}>
            {personName(person)}
        </Typography>
    </Box>
);
